'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import { useTranslations } from 'next-intl';
import { slugify } from '@/utils/url-helper';
import type { ProductBadgeModel } from '@/models/product-badge-model';

export interface FiltersProps {
  activeFilter: string;
  categories: { key: string; value: string }[];
  /** Badge filters shown before the categories. */
  badges?: Pick<ProductBadgeModel, 'key' | 'label'>[];
}

export function Filters({
  activeFilter: initialFilter,
  categories,
  badges = [],
}: Readonly<FiltersProps>) {
  const t = useTranslations('Filters');
  const router = useRouter();
  const searchParams = useSearchParams();
  const [filter, setFilter] = useState<string>(initialFilter);

  useEffect(() => {
    const queryFilter = searchParams.get('filter') || 'all-products';
    setFilter(queryFilter);
  }, [searchParams]);

  const handleFilterChange = (newFilter: string) => {
    const currentParams = new URLSearchParams(Array.from(searchParams.entries())); // Clone existing search params
    currentParams.set('filter', newFilter); // Update the filter param

    router.push(`?${currentParams.toString()}`, {});
  };

  // Badge labels come translated from Crystallize; category labels are translation keys.
  const filterOptions = [
    ...badges,
    ...categories
      .filter(Boolean)
      .map((category) => ({ key: category.key, label: t(category.value) })),
  ];

  const baseClasses =
    'inline-block whitespace-nowrap rounded-full border-0 bg-white p-2 px-6 font-bold uppercase sm:px-4 text-neutral-100 shadow-[inset_0_0_0_1px_#000] transition-[background-color,box-shadow] duration-200 ease-in hover:bg-primary-100 hover:shadow-[inset_0_0_0_2px_#FECB3F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#406AFF]';

  return (
    <div className="relative">
      <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-14 bg-gradient-to-l from-white" />
      <div
        className="filters -mx-1 max-w-full overflow-x-scroll px-1 py-1"
        style={{
          scrollbarWidth: 'none', // Hide scrollbar in Firefox
          msOverflowStyle: 'none', // Hide scrollbar in IE/Edge
        }}
      >
        {/* The tabs sit straight in the tablist, as ARIA expects; a list would need its items
          hidden from assistive technology. */}
        <div
          className="flex items-center gap-2 py-3 md:p-0 lg:p-0"
          role="tablist"
          aria-label={t('all-products')}
          style={{
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'all-products'}
            onClick={() => handleFilterChange('all-products')}
            className={twMerge(
              baseClasses,
              filter === 'all-products' && 'bg-primary-100 shadow-[inset_0_0_0_1px_#000]',
            )}
            data-tile-element-id="filter-all-products"
          >
            {t('all-products')}
          </button>
          {filterOptions.map((filterOption) => (
            <button
              key={filterOption.key}
              type="button"
              role="tab"
              aria-selected={filter === filterOption.key}
              onClick={() => handleFilterChange(filterOption.key)}
              className={twMerge(
                baseClasses,
                filter === filterOption.key && 'bg-primary-100 shadow-[inset_0_0_0_1px_#000]',
              )}
              data-tile-element-id={`filter-${slugify(filterOption.key)}`}
            >
              {filterOption.label}
            </button>
          ))}
        </div>
      </div>
      <style>
        {`
          .filters::-webkit-scrollbar {
            display: none; /* Hide scrollbar for Chrome, Safari, and Opera */
          }
        `}
      </style>
    </div>
  );
}
